import React from "react";
import { Controller } from "react-hook-form";
import { Card } from "../../../../components/card";
import { Input } from "../../../../components/input";
import { Label } from "../../../../components/label";
import { Textarea } from "../../../../components/textarea";
import { SelectV2 } from "../../../../components/select-v2";
import { SURVEY_CATEGORIES } from "../../data/constants";
import { DatePickerV3 } from "../../../../components/date-picker-v3";
import { FormErrorText } from "../../../../components/form/FormErrorText";
import { useGetNeighborhoodOptions } from "../../../common/public-options";
import { authStorage } from "../../../auth/shared/storage/auth-storage";
import { POINT_OPTIONS, SURVEY_FIELD_ERROR_CLASS_NAME } from "../../data/surveyForm.constants";
import type { SurveyFormProps } from "../../types/surveyForm.types";
import {
  formatSurveyCategoryLabel,
  getSurveyDisabledFields,
  getSurveyPointsErrorMessage,
  isPresetSurveyPoints,
} from "../../utils/surveyFormFields";

export function SurveyForm({ form, isPublished = false, disabledFields = {} }: SurveyFormProps) {
  const {
    control,
    formState: { errors },
  } = form;
  const points = form.watch("points");
  const organizationId = React.useMemo(() => authStorage.load()?.user.organization_id, []);
  const { neighborhoodOptions, fetchNeighborhoodOptions } =
    useGetNeighborhoodOptions(organizationId);
  const [isCustomPoints, setIsCustomPoints] = React.useState(() => !isPresetSurveyPoints(points));

  React.useEffect(() => {
    if (!form.formState.isDirty) {
      setIsCustomPoints(!isPresetSurveyPoints(points));
    }
  }, [form.formState.isDirty, points]);
  React.useEffect(() => {
    void fetchNeighborhoodOptions();
  }, [fetchNeighborhoodOptions]);

  const pointsError = getSurveyPointsErrorMessage(errors);
  const resolvedDisabledFields = getSurveyDisabledFields(disabledFields, isPublished);
  const disableEndTimeThrough = React.useMemo(() => {
    const yesterday = new Date();
    yesterday.setHours(0, 0, 0, 0);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }, []);

  return (
    <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-xl">
      <div className="p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Survey Details
        </h3>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label
                htmlFor="title"
                className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300"
              >
                Survey Title <span className="text-red-600 dark:text-red-400">*</span>
              </Label>
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <Input
                    id="title"
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.value)
                    }
                    onBlur={field.onBlur}
                    placeholder="Enter survey title"
                    className={`mt-2 bg-[#F3F3F5] dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${errors.title ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                    disabled={resolvedDisabledFields.title}
                  />
                )}
              />
              <FormErrorText message={errors.title?.message} />
            </div>
            <div>
              <Label
                htmlFor="category"
                className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300"
              >
                Category
              </Label>
              <Controller
                control={control}
                name="category"
                render={({ field }) => (
                  <SelectV2
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select category"
                    disabled={resolvedDisabledFields.category}
                    options={SURVEY_CATEGORIES.map((cat) => ({
                      label: formatSurveyCategoryLabel(cat),
                      value: cat,
                    }))}
                    triggerClassName={`mt-2 bg-[#F3F3F5] dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${errors.category ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                  />
                )}
              />
              <FormErrorText message={errors.category?.message} />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300">
              Neighborhood
            </Label>
            <Controller
              control={control}
              name="organizationPopulationId"
              render={({ field }) => (
                <SelectV2
                  value={field.value ?? ""}
                  onChange={(value) => field.onChange(value || null)}
                  placeholder="Select neighborhood"
                  disabled={resolvedDisabledFields.title}
                  options={neighborhoodOptions}
                  triggerClassName="mt-2 bg-[#F3F3F5] dark:bg-gray-700"
                />
              )}
            />
          </div>
          <div>
            <Label
              htmlFor="description"
              className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300"
            >
              Description
            </Label>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Textarea
                  id="description"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  placeholder="Enter survey description"
                  className={`mt-2 bg-[#F3F3F5] dark:bg-gray-700 resize-none ${errors.description ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                  rows={3}
                  disabled={resolvedDisabledFields.description}
                />
              )}
            />
            <FormErrorText message={errors.description?.message} />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label
                htmlFor="points"
                className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300"
              >
                Points
              </Label>
              <Controller
                control={control}
                name="points"
                render={({ field }) =>
                  isCustomPoints ? (
                    <Input
                      id="points"
                      type="number"
                      min="0"
                      max="1500"
                      value={Number.isNaN(field.value) ? "" : field.value}
                      onChange={(event) => {
                        const { value } = event.target;
                        field.onChange(value === "" ? Number.NaN : Number.parseInt(value, 10));
                      }}
                      onBlur={field.onBlur}
                      placeholder="Enter points (0-1500)"
                      className={`mt-2 bg-[#F3F3F5] dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${pointsError ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                      autoFocus
                      disabled={resolvedDisabledFields.points}
                    />
                  ) : (
                    <SelectV2
                      value={String(field.value)}
                      onChange={(value) => {
                        if (value === "custom") {
                          setIsCustomPoints(true);
                          field.onChange(Number.NaN);
                          return;
                        }

                        setIsCustomPoints(false);
                        field.onChange(Number.parseInt(value, 10));
                      }}
                      options={POINT_OPTIONS}
                      placeholder="Select points"
                      className="mt-2"
                      disabled={resolvedDisabledFields.points}
                      triggerClassName={`bg-[#F3F3F5] dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${pointsError ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                    />
                  )
                }
              />
              <FormErrorText message={pointsError} />
            </div>
            <div>
              <Label
                htmlFor="endTime"
                className="text-sm font-semibold text-[#0A0A0A] dark:text-gray-300"
              >
                End Time
              </Label>
              <Controller
                control={control}
                name="endTime"
                render={({ field }) => (
                  <DatePickerV3
                    value={field.value}
                    onChange={field.onChange}
                    disabled={resolvedDisabledFields.endTime}
                    disableToDate={disableEndTimeThrough}
                    className={`mt-2 h-10 bg-[#F3F3F5] dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${errors.endTime ? SURVEY_FIELD_ERROR_CLASS_NAME : ""}`}
                    placeholder="MM/dd/yyyy"
                    dateFormat="MM/dd/yyyy"
                  />
                )}
              />
              <FormErrorText message={errors.endTime?.message} />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default SurveyForm;
